import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Periode } from "@/types";
import {
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  Globe,
  GraduationCap,
  Handshake,
  Info,
  Loader2,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import LogoSNBP from "@/assets/logos/snbp.png";
import LogoSNBT from "@/assets/logos/snbt.png";

type PeriodeItemProps = {
  periode: Periode;
  namaJalur: string;
  isSelected: boolean;
  isPending: boolean;
  contentJalur?: any;
  jenjangMap: Record<string, string>;
  onSelect: () => void;
};

export function PeriodeItem({
  periode,
  // namaJalur,
  isSelected,
  isPending,
  contentJalur,
  jenjangMap,
  onSelect,
}: PeriodeItemProps) {
  const { t } = useTranslation();

  const slug = periode?.jalur_jenis?.nama_jalur_jenis_slug ?? "";
  const isInternasional = slug === "internasional";
  const isPascasarjana = slug === "pascasarjana";
  const isStudentExchange = slug === "studentexchange";
  const isSbmjm = [
    "cbt",
    "talentscouting",
    "prestasi",
    "kepemimpinan",
    "sekolahlab",
    "skorutbk",
  ].includes(slug);
  const isJalurSnbp = slug === "snbp";
  const isJalurSnbt = slug === "snbt";
  const isJalurNasional = periode.jalur_jenis?.id_ref_jalur == "1";

  const isBeforePengumuman =
    periode.is_jalur_siswa_lulus &&
    isSbmjm &&
    periode.waktu_pengumuman_kelulusan &&
    periode.waktu_current_server &&
    new Date(periode.waktu_current_server) <
      new Date(periode.waktu_pengumuman_kelulusan);

  const showLulusBadge = periode.is_jalur_siswa_lulus && !isBeforePengumuman;
  // const title = isInternasional ? periode?.nama_jalur_jenis : namaJalur;
  const title = periode?.nama_jalur_jenis;
  const description = periode?.keterangan ?? contentJalur?.description;

  return (
    // Wrapper div harus h-full agar mengikuti tinggi grid parent
    <div onClick={onSelect} className="group/item h-full flex flex-col">
      <Card
        className={cn(
          "group flex flex-col h-full justify-between cursor-pointer overflow-hidden border transition-all duration-300",
          "hover:shadow-xl hover:-translate-y-1 dark:hover:shadow-slate-900/50",
          isSelected
            ? "ring-2 ring-primary border-primary"
            : "border-slate-200 dark:border-slate-800",
          showLulusBadge
            ? "bg-green-50/40 border-green-200 dark:bg-green-900/10 dark:border-green-900"
            : "bg-white dark:bg-slate-900",
        )}
      >
        {/* Decorative Line */}
        <div
          className={cn(
            "absolute top-0 left-0 w-1 h-full transition-colors z-10",
            showLulusBadge ? "bg-green-500" : "bg-primary",
          )}
        />

        <CardHeader className="pb-2 pl-6 pt-5">
          <div className="flex justify-between items-start gap-3">
            <div className="w-full space-y-2">
              {/* Badges Area */}
              <div className="flex flex-wrap items-center gap-2 min-h-[24px]">
                {isInternasional && (
                  <Badge
                    variant="outline"
                    className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800 gap-1 px-2 py-0.5 text-[10px]"
                  >
                    <Globe className="w-3 h-3" />
                    International
                  </Badge>
                )}
                {!isInternasional && periode.gelombang_show && (
                  <Badge
                    variant="secondary"
                    className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 gap-1 px-2 py-0.5 text-[10px] font-normal"
                  >
                    <CalendarClock className="w-3 h-3" />
                    Gel. {periode.gelombang_show}
                  </Badge>
                )}
                {isPascasarjana ? (
                  periode.is_internasional_kerjasama ? (
                    <Badge
                      variant="outline"
                      className="bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800 gap-1 px-2 py-0.5 text-[10px]"
                    >
                      <Handshake className="w-3 h-3" />
                      {t("siswa.layout.dashboard.periode.type-kerjasama")}
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800 gap-1 px-2 py-0.5 text-[10px]"
                    >
                      <Users className="w-3 h-3" />
                      Reguler
                    </Badge>
                  )
                ) : (
                  ""
                )}
                {showLulusBadge && (
                  <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1 px-2 py-0.5 text-[10px]">
                    <CheckCircle2 className="w-3 h-3" />
                    Lulus
                  </Badge>
                )}
              </div>

              <div>
                {/* Title */}
                <div className="text-lg font-bold text-slate-800 dark:text-slate-100 leading-snug group-hover/item:text-primary dark:group-hover/item:text-primary transition-colors line-clamp-2 h-[3.2rem]">
                  <div className="flex items-center gap-3">
                    {isJalurSnbp && (
                      <img src={LogoSNBP} alt="logo-snbp" width={70} />
                    )}
                    {isJalurSnbt && (
                      <img src={LogoSNBT} alt="logo-snbt" width={70} />
                    )}
                    <span>{title}</span>
                  </div>
                  <span className="ml-1 text-base italic font-bold text-primary">
                    {isPascasarjana
                      ? periode.is_internasional_kerjasama
                        ? `(${t(
                            "siswa.layout.dashboard.periode.type-kerjasama",
                          )})`
                        : "(REGULER)"
                      : ""}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 pl-6 flex-1 flex flex-col">
          {/* Jenjang Section */}
          <div className="space-y-1.5 shrink-0">
            <div className="flex items-center gap-2 text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
              <GraduationCap className="w-3.5 h-3.5 text-primary" />
              <span>{t("siswa.layout.dashboard.periode.program.title")}</span>
            </div>
            {isStudentExchange ? (
              <div className="flex flex-wrap gap-1.5">
                {periode.sys_daftar_program?.map((program) => {
                  return (
                    <Badge
                      key={program.id_sys_daftar_program}
                      variant="outline"
                      className="border-slate-200 text-slate-600 bg-slate-50/50 dark:border-slate-700 dark:text-slate-300 dark:bg-slate-800/50 text-[10px] px-2"
                    >
                      {program.ref_daftar_program.nama_program}
                    </Badge>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {periode?.jenjang_buka && periode.jenjang_buka.length > 0 ? (
                  periode.jenjang_buka.slice(0, 4).map((jenjang, idx) => {
                    const key = jenjang?.nama_jenjang_pendidikan;
                    const label =
                      isInternasional && key && jenjangMap[key]
                        ? jenjangMap[key]
                        : (key ?? "-");

                    return (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="border-slate-200 text-slate-600 bg-slate-50/50 dark:border-slate-700 dark:text-slate-300 dark:bg-slate-800/50 text-[10px] px-2"
                      >
                        {label}
                      </Badge>
                    );
                  })
                ) : (
                  <span className="text-xs text-slate-400 italic">-</span>
                )}
                {periode?.jenjang_buka && periode.jenjang_buka.length > 4 && (
                  <span className="text-[10px] text-slate-400 dark:text-slate-500 flex items-center">
                    +{periode.jenjang_buka.length - 4} lainnya
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Separator halus */}
          <div className="h-px w-full bg-slate-100 dark:bg-slate-800 my-1" />

          {/* Description */}
          <div className="relative flex gap-2 items-start flex-1 cursor-default">
            {/* Ikon ikut berubah warna saat group di-hover */}
            <Info className="w-4 h-4 text-slate-400 dark:text-slate-500 mt-0.5 shrink-0 transition-colors duration-200 group-hover/item:text-primary" />

            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed line-clamp-3 transition-all duration-300 group-hover/item:text-slate-900 dark:group-hover/item:text-slate-100">
              {isJalurNasional
                ? description
                  ? description
                  : "Silakan lakukan daftar kembali untuk melengkapi berkas administrasi dan mengonfirmasi status kelulusan Anda"
                : description
                  ? description
                  : "Silahkan pilih jalur seleksi ini untuk melihat detail dan melakukan pendaftaran."}
            </p>
          </div>
        </CardContent>

        <CardFooter className="pt-2 pl-6 pb-5 mt-auto">
          <Button
            className={cn(
              "w-full h-10 justify-between hover:bg-primary hover:text-white transition-all shadow-sm",
              showLulusBadge
                ? "bg-green-600 hover:bg-green-700 text-white"
                : "bg-slate-50 text-slate-900 border border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:hover:border-primary",
            )}
            disabled={isPending && isSelected}
          >
            {isPending && isSelected ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-xs">Processing...</span>
              </div>
            ) : (
              <>
                <span className="font-semibold text-sm">
                  {showLulusBadge
                    ? isJalurNasional
                      ? "Daftar Kembali"
                      : t("siswa.layout.dashboard.periode.action.lihat")
                    : t("siswa.layout.dashboard.periode.action.pilih")}
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
